import React, { FunctionComponent } from 'react';
import { Table, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { map, pathOr, addIndex, isEmpty, isNil } from 'ramda';

import { ClassProp } from 'helpers/types';

import styles from './DiscoveredVenueTable.styles';

export type CardNetworkId = {
  cardNetwork: string;
  cardNetworkExternalId: string;
  cardNetworkExternalIdType: string;
};

export type Address = {
  address1: string;
  city: string;
  state: string;
  postal: string;
  country: string;
};

export type DiscoveredVenue = {
  name: string;
  transactions: number;
  address: Address;
  cardNetworkId: CardNetworkId;
};

type TableLayout = {
  label: string;
  source: any;
};

type Props = {
  data: DiscoveredVenue[];
  classes: ClassProp<typeof styles>;
};

const mapIndexed = addIndex(map);

const DISCOVERED_VENUE_TABLE_LAYOUT: TableLayout[] = [
  {
    label: 'Name',
    source: ['name'],
  },
  {
    label: 'Transactions',
    source: ['transactions'],
  },
  {
    label: 'Address',
    source: ['address', 'address1'],
  },
  {
    label: 'City',
    source: ['address', 'city'],
  },
  {
    label: 'State',
    source: ['address', 'state'],
  },
  {
    label: 'Zip',
    source: ['address', 'postal'],
  },
  {
    label: 'CN',
    source: ['cardNetworkId', 'cardNetwork'],
  },
  {
    label: 'CN EID',
    source: ['cardNetworkId', 'cardNetworkExternalId'],
  },
  {
    label: 'CN Type',
    source: ['cardNetworkId', 'cardNetworkExternalIdType'],
  },
];

const isNotValid = (data: DiscoveredVenue[]) => isEmpty(data) || isNil(data);

const DiscoveredVenueTable: FunctionComponent<Props> = ({ data, classes }) => {
  const renderTableHeader = (classes: ClassProp<typeof styles>) => (
    <Table.Header>
      <Table.Row>
        {mapIndexed(renderHeaderCell(classes), DISCOVERED_VENUE_TABLE_LAYOUT)}
      </Table.Row>
    </Table.Header>
  );

  const renderHeaderCell = (classes: ClassProp<typeof styles>) => (
    { label, source }: any,
    idx: number,
  ) => (
    <Table.HeaderCell className={classes.tableHeader} key={`${source + idx}`}>
      {label}
    </Table.HeaderCell>
  );

  const renderBodyCell = (record: Record<string, unknown>) => (
    { source }: any,
    idx: number,
  ) => {
    let fieldValue = pathOr('', source, record);
    if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(', ');
    }
    return <Table.Cell key={`${source + idx}`}>{fieldValue}</Table.Cell>;
  };

  const renderTableRow = (classes: ClassProp<typeof styles>) => (
    source: any,
    idx: number,
  ) => (
    <Table.Row key={`${idx}`} className={classes.selectableRow}>
      {mapIndexed(renderBodyCell(source), DISCOVERED_VENUE_TABLE_LAYOUT)}
    </Table.Row>
  );

  if (isNotValid(data)) {
    return (
      <Header as="h3" className={classes.header} content="No Venues found!" />
    );
  }

  return (
    <Table className={classes.container} selectable sortable>
      {renderTableHeader(classes)}
      <Table.Body>{mapIndexed(renderTableRow(classes), data)}</Table.Body>
    </Table>
  );
};

export default withStyles(styles)(DiscoveredVenueTable);
